// src/pages/Instalaciones.js
import React, { useState, useEffect, useCallback } from 'react';
import '../styles/instalaciones.css';

export default function Instalaciones() {
    const [texto, setTexto] = useState(null);        // /data/instalacionesText.json
    const [imagenes, setImagenes] = useState([]);    // /data/instalacionesImg.json
    const [imagenSel, setImagenSel] = useState(null);
    const [idxSel, setIdxSel] = useState(0);

    const [cargandoTexto, setCargandoTexto] = useState(true);
    const [cargandoImgs, setCargandoImgs] = useState(true);
    const [errorTexto, setErrorTexto] = useState(false);
    const [errorImgs, setErrorImgs] = useState(false);

    // 1) Cargar JSON de texto (primero para tener los copys de UI)
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const rText = await fetch('/data/instalacionesText.json', { cache: 'no-store' });
                if (!rText.ok) throw new Error('HTTP ' + rText.status);
                const dataText = await rText.json();
                if (cancel) return;
                setTexto(dataText);
                if (dataText?.seoTitle) document.title = dataText.seoTitle;
            } catch (e) {
                console.error('[Instalaciones] Error texto:', e);
                if (!cancel) setErrorTexto(true);
            } finally {
                if (!cancel) setCargandoTexto(false);
            }
        })();
        return () => { cancel = true; };
    }, []);

    // 2) Cargar JSON de imágenes (después, usando ya los textos de UI)
    useEffect(() => {
        if (!texto) return; // esperamos a tener copys para mostrar mensajes
        let cancel = false;
        (async () => {
            try {
                const rImgs = await fetch('/data/instalacionesImg.json', { cache: 'no-store' });
                if (!rImgs.ok) throw new Error('HTTP ' + rImgs.status);
                const dataImgs = await rImgs.json();
                if (cancel) return;
                const arr = Array.isArray(dataImgs) ? dataImgs : [];
                setImagenes(arr);
                if (arr.length) {
                    setIdxSel(0);
                }
            } catch (e) {
                console.error('[Instalaciones] Error imágenes:', e);
                if (!cancel) setErrorImgs(true);
            } finally {
                if (!cancel) setCargandoImgs(false);
            }
        })();
        return () => { cancel = true; };
    }, [texto]);

    // Overlay y navegación
    const abrirOverlay = (img, index) => { setImagenSel(img); setIdxSel(index); };
    const cerrarOverlay = () => setImagenSel(null);

    const siguiente = useCallback(() => {
        setIdxSel(prev => {
            const nuevo = (prev + 1) % (imagenes.length || 1);
            setImagenSel(imagenes[nuevo]);
            return nuevo;
        });
    }, [imagenes]);

    const anterior = useCallback(() => {
        setIdxSel(prev => {
            const nuevo = (prev - 1 + (imagenes.length || 1)) % (imagenes.length || 1);
            setImagenSel(imagenes[nuevo]);
            return nuevo;
        });
    }, [imagenes]);

    // Teclas ← → Esc cuando está abierto
    useEffect(() => {
        const onKey = (e) => {
            if (!imagenSel) return;
            if (e.key === 'ArrowRight') siguiente();
            else if (e.key === 'ArrowLeft') anterior();
            else if (e.key === 'Escape') cerrarOverlay();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [imagenSel, siguiente, anterior]);

    // 3) Estados globales
    if (cargandoTexto || !texto) {
        // Evitamos texto hardcodeado mientras no tenemos JSON de UI
        return <div className="instalaciones-wrapper" aria-busy="true" style={{ minHeight: 120 }} />;
    }

    if (errorTexto) {
        return (
            <div className="instalaciones-wrapper">
                <p role="alert">{texto.ui?.loadError}</p>
            </div>
        );
    }

    return (
        <div className="instalaciones-wrapper">
            {/* H1 controlado por JSON de texto */}
            <h1 className="titulo-instalaciones">{texto.sectionTitle}</h1>
            <hr className="linea-separadora" />

            {texto.intro && <p className="intro-instalaciones">{texto.intro}</p>}

            {/* Estado de carga / error / vacío para imágenes */}
            {cargandoImgs ? (
                <p className="intro-instalaciones">{texto.ui?.loadingText}</p>
            ) : errorImgs ? (
                <p className="intro-instalaciones" role="alert">{texto.ui?.loadError}</p>
            ) : !imagenes.length ? (
                <p className="intro-instalaciones">{texto.ui?.emptyText}</p>
            ) : (
                <div className="masonry-grid">
                    {imagenes.map((img, idx) => (
                        <div
                            key={img.id ?? idx}
                            className="masonry-item"
                            onClick={() => abrirOverlay(img, idx)}
                            tabIndex={0}
                            role="button"
                            aria-label={`${texto.ui?.openImageAria} ${img.id ?? idx + 1}`}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') abrirOverlay(img, idx);
                            }}
                        >
                            <img
                                src={img.src}
                                alt={img.descripcion || `${texto.ui?.imageAltPrefix} ${img.id ?? (idx + 1)}`}
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>
            )}

            {texto.visitMessage && (
                <p className="mensaje-visita"><strong>{texto.visitMessage}</strong></p>
            )}

            {/* Overlay accesible */}
            {imagenSel && (
                <div
                    className="overlay overlay-full"
                    role="dialog"
                    aria-modal="true"
                    aria-label={texto.ui?.overlayLabel}
                    onClick={cerrarOverlay}
                >
                    <button
                        className="btn-navegacion izquierda"
                        onClick={(e) => { e.stopPropagation(); anterior(); }}
                        aria-label={texto.ui?.prevAria}
                    >
                        &#10094;
                    </button>

                    <div className="overlay-contenido" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={imagenSel.src}
                            alt={`${texto.ui?.enlargedAltPrefix} ${imagenSel.id ?? ''}`}
                        />
                        {imagenSel.descripcion && <p>{imagenSel.descripcion}</p>}
                        <button
                            className="btn-cerrar"
                            onClick={cerrarOverlay}
                            aria-label={texto.ui?.closeAria}
                            title={texto.ui?.closeAria}
                        >
                            ×
                        </button>
                    </div>

                    <button
                        className="btn-navegacion derecha"
                        onClick={(e) => { e.stopPropagation(); siguiente(); }}
                        aria-label={texto.ui?.nextAria}
                    >
                        &#10095;
                    </button>
                </div>
            )}
        </div>
    );
}
